import { Link } from '@tanstack/react-router'
import { MdArrowForward, MdKeyboardArrowDown } from 'react-icons/md'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '~/components/ui/dropdown-menu'

type HelpCentreMenuItem = {
	label: string
	anchor?: string
	slug?: string
}

type HelpCentreSection = {
	label: string
	slug: string
	anchor?: string
	items?: HelpCentreMenuItem[]
}

type HelpCentreColumn = {
	id: string
	sections: HelpCentreSection[]
}

/**
 * Single source of truth for help centre menu structure.
 * Columns map directly to Figma layout (3 columns, 200px each).
 */
const helpCentreMenu: HelpCentreColumn[] = [
	{
		id: 'col-left',
		sections: [
			{
				label: 'Chat',
				slug: 'chat',
				items: [
					{ label: 'Threads', anchor: 'threads' },
					{ label: 'Spaces', anchor: 'spaces' },
				],
			},
			{ label: 'Spark Apps', slug: 'spark-apps' },
		],
	},
	{
		id: 'col-middle',
		sections: [
			{
				label: 'AI Studio',
				slug: 'ai-studio',
				items: [
					{ label: 'Datasets', anchor: 'datasets' },
					{ label: 'Projects', anchor: 'projects' },
				],
			},
		],
	},

	{
		id: 'col-right',
		sections: [
			{
				label: 'Support',
				slug: 'support',
				items: [
					{ label: 'Glossary', slug: 'glossary' },
					{ label: 'Data guidelines', slug: 'data-guidelines' },
					{ label: 'Roadmap and releases', slug: 'roadmap-and-releases' },
				],
			},
		],
	},
]

/** Derive valid page slugs from menu structure (plus spark-ai as default landing, updates as linked page) */
const validSlugs = new Set(['spark-ai', 'updates', ...helpCentreMenu.flatMap((col) => col.sections.flatMap((s) => [s.slug, ...(s.items?.map((i) => i.slug).filter(Boolean) ?? [])]))])

export function isHelpCentrePageSlug(slug: string) {
	return validSlugs.has(slug)
}

export function NavHelpCentre() {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className="flex h-[32px] cursor-pointer items-center gap-[4px] border border-transparent px-[12px] font-semibold text-white outline-none hover:bg-navy-75 active:border-white data-[state=open]:border-white"
					aria-label="Help Centre menu"
				>
					<span>Help Centre</span>
					<MdKeyboardArrowDown className="size-[20px] shrink-0 text-white" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				sideOffset={13}
				className="w-[696px] border-navy-75 bg-navy-100 p-0 text-white max-header:hidden"
				skipExitAnimation
			>
				<div className="pt-[2px]">
					<div className="h-[2px] w-full bg-light-blue-100" />
					<div className="flex flex-col items-start gap-[24px] px-[24px] pt-[22px] pb-[24px]">
						<div className="flex flex-col items-start gap-[12px]">
							<DropdownMenuItem
								asChild
								className="h-auto p-0 focus:bg-transparent focus:text-light-blue-100"
							>
								<Link
									to="/help-centre/$"
									params={{ _splat: 'spark-ai' }}
									className="flex cursor-pointer items-center gap-[8px] font-display font-normal text-[24px] text-white leading-[120%] tracking-[-0.24px] hover:text-light-blue-100 active:text-light-blue-75 active:underline"
								>
									<MdArrowForward className="size-[20px] shrink-0 text-light-blue-100" />
									<span>Help Centre</span>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem
								asChild
								className="h-auto p-0 focus:bg-transparent focus:text-light-blue-100"
							>
								<Link
									to="/help-centre/$"
									params={{ _splat: 'updates' }}
									className="cursor-pointer pl-[30px] font-600 text-[16px] text-white tracking-[-0.12px] hover:text-light-blue-100 hover:underline active:text-light-blue-100"
								>
									What's new
								</Link>
							</DropdownMenuItem>
						</div>
						<div className="h-px w-full bg-dark-blue-75" />
						<div className="flex h-[140px] items-start gap-[24px]">
							{helpCentreMenu.map((column) => (
								<div
									key={column.id}
									className="flex w-[200px] flex-col gap-[24px]"
								>
									{column.sections.map((section) => (
										<div
											key={section.slug}
											className="flex flex-col gap-[12px]"
										>
											<DropdownMenuItem
												asChild
												className="h-auto p-0 focus:bg-transparent focus:text-light-blue-100"
											>
												<Link
													to="/help-centre/$"
													params={{ _splat: section.slug }}
													hash={section.anchor}
													className="flex cursor-pointer items-center gap-[8px] font-700 text-[18px] text-white tracking-[-0.135px] hover:text-light-blue-100 hover:underline active:text-light-blue-75"
												>
													<MdArrowForward className="size-[16px] shrink-0 text-light-blue-100" />
													<span className="leading-[140%]">{section.label}</span>
												</Link>
											</DropdownMenuItem>

											{section.items && section.items.length > 0 && (
												<div className="flex flex-col gap-[12px] pl-[24px]">
													{section.items.map((item) => (
														<DropdownMenuItem
															key={item.label}
															asChild
															className="h-auto p-0 focus:bg-transparent focus:text-light-blue-100"
														>
															<Link
																to="/help-centre/$"
																params={{ _splat: item.slug ?? section.slug }}
																hash={item.anchor}
																className="cursor-pointer font-600 text-[16px] text-white tracking-[-0.12px] hover:text-light-blue-100 hover:underline active:text-light-blue-100"
															>
																{item.label}
															</Link>
														</DropdownMenuItem>
													))}
												</div>
											)}
										</div>
									))}
								</div>
							))}
						</div>
					</div>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
